'use client';

import { getPhaserAttributesFromUrl, phaserAttributesSet } from '@modules/phaser';
import { InteractionHeader, type InteractionHeaderButtonProps, type InteractionHeaderProps } from '@tuic/web-compositions/my-trips/InteractionHeader';
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { HistoryAwareBackLink } from '../../../../../app/[locale]/[bookingCode]/_components/history-aware-back-link';
import { urls } from '@lib/urls';
import { useClientTranslation } from '@lib/i18n/client';
import { useThirdPartyEvent } from '@tuic/lib/src/hooks';
import { EVENT_PHASER_OPENER } from '@modules/phaser';
import { logger } from '@tuic/logger';
import { telemetryToClickAttributes } from '@modules/prism-client/lib/telemetry';
import { Mxp365Link } from '../../mxp365/Mxp365Link';
import { useNotificationCount } from '@lib/hooks/use-notification-count';
import type { ReactElement } from 'react';
import { Locale } from '@tuic/i18n';
import { ButtonElementProps } from '../../content-transformers/button';
import { ComponentBlock } from '@modules/prism-client/types/types';
export type InteractionHeaderBlockData = Omit<InteractionHeaderProps, 'leftButton' | 'rightButtons' | 'phaserButton'> & {
  locale: Locale;

  /** Left side button (typically back button) */
  leftButton?: ButtonElementProps;

  /** Right side buttons (home, activities, favorites, etc.) */
  rightButtons?: ButtonElementProps[];

  /** Phaser button configuration */
  shouldRenderPhaser?: boolean;

  /** Current path of the page */
  currentPath: string;
};

/**
 * InteractionHeaderBlock - Business logic wrapper for InteractionHeader UI component
 *
 * Responsibilities:
 * - Transform ButtonElementProps into InteractionHeaderButton with React element children
 * - Handle different link types (regular links, action links like history-back/logout)
 * - Provide a default back button when no leftButton specified
 * - Manage phaser state and integration
 */
export const InteractionHeaderBlock: ComponentBlock<InteractionHeaderBlockData> = ({
  data,
  fallback
}) => {
  const {
    t
  } = useClientTranslation();
  const {
    invoke: openPhaser
  } = useThirdPartyEvent(EVENT_PHASER_OPENER);
  const [isPhased, setIsPhased] = useState<boolean>(false);

  // Track phaser state from URL
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const attrs = getPhaserAttributesFromUrl(window.location.href);
    setIsPhased(phaserAttributesSet(attrs));
  }, []);

  // Phaser button configuration
  const phaserButton = useMemo(() => {
    if (!data?.shouldRenderPhaser) {
      return undefined;
    }
    return {
      children: <button onClick={() => queueMicrotask(() => openPhaser())} />,
      isActive: isPhased,
      label: 'Phaser'
    };
  }, [data?.shouldRenderPhaser, isPhased, openPhaser]);

  // Extract mxp365 token from any mxp365 link in the right buttons for notification count
  const mxp365Token = useMemo(() => {
    const mxp365Button = data?.rightButtons?.find(b => b.link?.type === 'mxp365');
    if (mxp365Button?.link?.type === 'mxp365') {
      try {
        const url = new URL(mxp365Button.link.url);
        return url.searchParams.get('token') ?? undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }, [data?.rightButtons]);
  const {
    chatMessages = 0,
    notifications = 0
  } = useNotificationCount({
    token: mxp365Token
  });
  if (fallback) {
    return <InteractionHeader fallback />;
  }
  if (!data) {
    return null;
  }

  // Transform left button - default to history-back button if not provided
  const leftButton: InteractionHeaderButtonProps | undefined = data.leftButton ? transformButtonToInteractionHeaderButton(data.leftButton, data) ?? undefined : {
    iconName: 'arrow-left',
    variant: 'secondary',
    label: t('Zurück'),
    children: <HistoryAwareBackLink tryHistoryBack={true} href={urls.index({
      locale: data.locale
    })} />
  };

  // Transform right buttons, adding notification badges based on MXP365 URL path
  const rightButtons: InteractionHeaderButtonProps[] | undefined = data.rightButtons?.map(button => {
    const headerButton = transformButtonToInteractionHeaderButton(button, data);
    if (!headerButton) return null;
    const badge = getNotificationBadge(button, {
      chatMessages,
      notifications
    });
    return badge ? {
      ...headerButton,
      badge
    } : headerButton;
  }).filter((button): button is InteractionHeaderButtonProps => button !== null);
  const hasAnyBadge = rightButtons?.some(b => b.badge !== undefined);
  return <InteractionHeader title={data.title} leftButton={leftButton} rightButtons={rightButtons} showNotificationDot={hasAnyBadge} phaserButton={phaserButton} data-sentry-element="InteractionHeader" data-sentry-component="InteractionHeaderBlock" data-sentry-source-file="InteractionHeaderBlock.tsx" />;
};

/**
 * Transform ButtonElementProps to InteractionHeaderButton
 * Creates appropriate React element children based on link type
 */
const transformButtonToInteractionHeaderButton = (button: ButtonElementProps, data: InteractionHeaderBlockData): InteractionHeaderButtonProps | null => {
  // Determine icon name from iconLeft or iconRight
  const iconName = button.iconLeft ?? button.iconRight;
  if (!iconName) {
    logger.warn('Button has no iconName (iconLeft or iconRight)', button);
    return null;
  }

  // Create a children element based on a link type
  const children = createButtonChildren(button, data);
  if (!children) {
    logger.warn('Could not create button children for button', button);
    return null;
  }
  return {
    iconName,
    variant: button.variant,
    on: button.on,
    label: button.label,
    children
  };
};

/**
 * Create React element children for a button based on the link type-Regular links → Link
 * - Action: history-back → HistoryAwareBackLink with tryHistoryBack
 * - Action: go-up → Link (navigate to a parent path)
 * - Action: logout → anchor with sign-out URL
 * - Other actions → button with generic action handler
 */
const createButtonChildren = (button: ButtonElementProps, data: InteractionHeaderBlockData): ReactElement | null => {
  const link = button.link;
  const trackingAttributes = telemetryToClickAttributes(button.telemetry);

  // No link - create a disabled/placeholder button
  if (!link) {
    return <button disabled {...trackingAttributes} />;
  }

  // Handle action links
  if (link.type === 'action') {
    switch (link.action) {
      case 'history-back':
        {
          return <HistoryAwareBackLink tryHistoryBack={true} href={urls.index({
            locale: data.locale
          })} {...trackingAttributes} />;
        }
      case 'go-up':
        {
          return <Link href={data.currentPath.split('/').slice(0, -1).join('/') || '/'} {...trackingAttributes} />;
        }
      case 'logout':
        {
          return (
            // because content is provided by InteractionHeaderButton:
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a href={urls.signOutWithBookingCode({
              locale: data.locale
            })} {...trackingAttributes} />
          );
        }
      default:
        {
          logger.warn(`Unknown action type: ${link.action}`);
          return <button onClick={() => {
            logger.warn(`Action handler not implemented: ${link.action}`);
          }} {...trackingAttributes} />;
        }
    }
  }
  if (link.type === 'mxp365') {
    return <Mxp365Link data={link} {...trackingAttributes} />;
  }

  // Handle regular links
  if (link.type === 'link') {
    return <Link href={link.href ?? '#'} {...trackingAttributes} />;
  }

  // Fallback for unknown link types
  logger.warn('Unknown link type', link);
  return <button disabled {...trackingAttributes} data-sentry-component="createButtonChildren" data-sentry-source-file="InteractionHeaderBlock.tsx" />;
};

/**
 * Determine notification badge for a button based on its MXP365 URL path.
 * Uses URL path matching instead of label text to be language-independent.
 */
const getNotificationBadge = (button: ButtonElementProps, counts: {
  chatMessages: number;
  notifications: number;
}): string | undefined => {
  if (button.link?.type !== 'mxp365') return undefined;
  try {
    const path = new URL(button.link.url).pathname;
    if (path.endsWith('/chat') && counts.chatMessages > 0) {
      return String(counts.chatMessages);
    }
    if (path.endsWith('/messages') && counts.notifications > 0) {
      return String(counts.notifications);
    }
  } catch {
    return undefined;
  }
  return undefined;
};